import { Component, type ReactNode } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { reportClientError } from '../lib/reportClientError'
import { Button, Title2, Text, tokens } from '@fluentui/react-components'

interface InnerProps {
  children: ReactNode
  /** Changes on every navigation. A change while the fallback is up clears it. */
  resetKey: string
  /** The only way out of a route that throws during render. */
  onGoHome: () => void
}

interface State {
  hasError: boolean
  error: Error | null
}

/**
 * The class half. React offers error boundaries only as classes, so the router
 * hooks live in the wrapper below and arrive here as props.
 */
class ErrorBoundaryInner extends Component<InnerProps, State> {
  state: State = { hasError: false, error: null }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, info: { componentStack?: string | null }) {
    // The console line stays: it is what a developer with the tab open reads,
    // and it costs nothing.
    console.error('[ErrorBoundary]', error, info.componentStack)
    // And the server hears about it, which until now nothing did. A crash left
    // no record anywhere off the machine it happened on, so a reported fault
    // arrived with no stack and nothing to correlate against.
    reportClientError(error, info.componentStack)
  }

  componentDidUpdate(prev: InnerProps) {
    // Clear on navigation.
    //
    // Without this the boundary LATCHED: once any render threw, every
    // subsequent route rendered the fallback too, because `hasError` was never
    // unset and the children stopped rendering. The one control offered was
    // "Reload page", which on a route that breaks during render reloads
    // straight back into the break. Browser Back did not help either — the URL
    // changed underneath a component that had stopped rendering its children.
    // For that session, the app was over.
    if (this.state.hasError && prev.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false, error: null })
    }
  }

  render() {
    if (!this.state.hasError) return this.props.children

    return (
      <div
        style={{
          padding: tokens.spacingHorizontalXXL,
          maxWidth: 600,
          margin: '80px auto',
          textAlign: 'center',
        }}
      >
        <Title2 style={{ marginBottom: tokens.spacingVerticalL }}>Something went wrong</Title2>
        <Text
          block
          style={{ marginBottom: tokens.spacingVerticalL, color: tokens.colorNeutralForeground3 }}
        >
          {this.state.error?.message || 'An unexpected error occurred.'}
        </Text>
        <div
          style={{ display: 'flex', gap: tokens.spacingHorizontalM, justifyContent: 'center' }}
        >
          {/* Home first. Reloading a route that breaks during render breaks
              again, so the recovery that actually works should not be the
              secondary action. */}
          <Button appearance="primary" onClick={this.props.onGoHome}>
            Back to home
          </Button>
          <Button
            onClick={() => {
              this.setState({ hasError: false, error: null })
              window.location.reload()
            }}
          >
            Reload page
          </Button>
        </div>
      </div>
    )
  }
}

export function ErrorBoundary({ children }: { children: ReactNode }) {
  const location = useLocation()
  const navigate = useNavigate()
  return (
    <ErrorBoundaryInner resetKey={location.key} onGoHome={() => navigate('/')}>
      {children}
    </ErrorBoundaryInner>
  )
}
